/** Platform specific implementations. */
import * as Api from 'mytours-read-api-typescript'
import { Middleware, ReducersMapObject } from 'redux'
import { connectRouter, RouterState } from 'connected-react-router'
import {
	AppMetaData,
	getCurrentLanguageCode,
	getStore,
	FileType,
	StoreState,
	DevicePlatform,
	DEVICE_PLATFORM_KEY_MOBILE_WEB,
} from 'mytours-read-common'
import { getUAParser } from '../../services/uaParser'
import { isOfflineError } from '../api/errors'

import { colorMix, darkenOrLighten, fade, isDark } from 'common_lib/services/color'
import { loggerMiddleware } from '../logging/index'
import { middleware as routerMiddleware } from '../navigation/index'
import settings from '../settings/functions'
import strings from '../../strings'
import { syncThemeStrings } from './themeStrings'
import { FileRec, GetAppShowResponse } from 'mytours-read-api-typescript'
import { ImageSizePreset } from 'common_lib/types'
import { generateImageUrl } from 'common_lib/utils'
import { getDemoModeState } from '../common/functions'
import { history } from '../navigation/history'

// Received from backend, declared in views/layouts/mobile_web.html.erb
declare const projectId: string
declare const token: string
declare const baseServer

interface FormattedDurationObject {
	days?: number
	hours?: number
	minutes?: number
}

export interface PlatformStoreState extends StoreState {
	router: RouterState // added by connectRouter(history)
}

/** Customise the Redux middleware for this platform */
export function customiseReduxMiddleware(middlewares: Middleware[]): Middleware[] {
	// navigation
	middlewares.push(routerMiddleware)
	// logs out redux store changes to console (dev only)
	middlewares.push(loggerMiddleware)
	return middlewares
}

export function customiseReducers(reducer: ReducersMapObject<PlatformStoreState>): ReducersMapObject<StoreState> {
	// TODO: where it's using?
	reducer.router = connectRouter(history)
	return reducer
}

/**
 * API configuration
 */

export function getDefaultApiConfiguration(): Api.Configuration {
	return new Api.Configuration({
		// these cannot be functions, this means we need to create a new Api when changing app id (new configuration)
		username: projectId,
		password: token,
	})
}

export const isDemoMode = (): boolean => getDemoModeState()

export const getApiHeaders = (): Record<string, string> => {
	const headers = {
		'Stqry-Project-Type': 'mobile_web',
	}
	if (getDemoModeState()) {
		headers['Stqry-Demo-Mode'] = 'true'
	}
	return headers
}

export const getDirectApiBaseUrl = (region?: string): string =>
	`https://${ENV.API_HOST.replace(`api-${ENV.STQRY_REGION}`, `api-${region || ENV.STQRY_REGION}`)}`

export const getCdnApiBaseUrl = (): string => (baseServer.startsWith('https://') ? baseServer : `https://${baseServer}`)

export const isRTL = (languageCode?: string): boolean => {
	if (!languageCode) {
		languageCode = getCurrentLanguage()
	}

	return ['he', 'ar', 'fa'].includes(languageCode)
}

/**
 * Strings / Device language
 */

export interface Style {
	'--background-color': string
	'--background-color-hover': string
	'--background-image': string
	'--content-background-image': string
	'--extended-background-color': string
	'--body-color': string
	'--body-color-hover': string
	'--extended-body-color': string
	'--secondary-color': string
	'--placeholder-bg': string
	'--nav-bg': string
	'--tab-bg': string
	'--tab-content-color': string
	'--tab-content-color-selected': string
	'--button-bg': string
	'--button-bg-hover': string
	'--button-action-bg': string
	'--warning-color': string
	'--button-secondary-bg': string
	'--button-secondary-bg-hover': string
	'--button-secondary-border': string
	'--border-base-rbg': string
	'--border-color': string
	'--border-light': string
	'--border-overlay': string
	'--map-content-shadow': string
	'--border-shadow': string
	'--card-box-shadow-1': string
	'--card-box-shadow-2': string
	'--card-box-shadow-3': string

	// Audio player — sc-143491. All optional so themes whose backend hasn't
	// been migrated yet keep rendering with the LESS-side fallbacks. Named
	// with explicit `-fullscreen-`, `-bar-`, `-transcript-` segments so the
	// LESS file can use `--audio-player-bg` as a per-mode local without
	// colliding with the platform-emitted tokens.
	'--audio-player-fullscreen-bg'?: string
	'--audio-player-fullscreen-color'?: string
	'--audio-player-bar-bg'?: string
	'--audio-player-bar-color'?: string
	'--audio-player-transcript-bg'?: string
	'--audio-player-transcript-color'?: string
	'--audio-player-now-playing'?: string
	'--audio-player-now-playing-border-width'?: string
	'--audio-player-inactive-opacity'?: string
	'--audio-player-next-label-opacity'?: string

	'--content-margin-horizontal'?: string
	'--max-page-width'?: string
	'--app-background-display'?: string
	// Retired theme field, still read as a fallback for the page background; see setUpStylesNewTheme.
	'--app-background-background-value'?: string
	'--page-background-color-rgb'?: string
	'--app-background-color-rgb'?: string
	'--title-bar-background-color-rgb'?: string
	'--tour-overview-header-end-background-color-rgb'?: string
	// The backdrop behind the centred content; see setUpStylesNewTheme.
	'--app-bg'?: string
	'--default-color'?: string
}

/**
 * Sets the language to display static strings in. Made into function as called from multiple places.
 * @param languageCode the language to display strings in
 */
export function changeLocalizationLanguage(languageCode: string) {
	// there is some issue with {languageCode}
	// use this method as callback and set local lang code
	strings.setLanguage(settings.appLanguage.currentLocale || languageCode)
}

// theme.css emits a colour's rgb companion as a space-separated triplet (`255 255 255`) for use
// inside rgb(). Returns undefined for anything unusable so the caller can fall back: the companion
// is absent for keyword and 8-digit-alpha colours, and is an unresolved `var(...)` string when read
// off an element whose stylesheet never supplied it.
const rgbTripletToHex = (triplet: string): string | undefined => {
	const channels = triplet
		.trim()
		.split(/[\s,]+/)
		.map(Number)
	if (channels.length !== 3) return undefined
	if (channels.some(channel => !Number.isInteger(channel) || channel < 0 || channel > 255)) return undefined

	return `#${channels.map(channel => channel.toString(16).padStart(2, '0')).join('')}`
}

// When new_theming_system is true, the project's resolved theme is delivered
// as a stylesheet (mobile_web.html.erb:91 → `/<theme-version>/theme.css`).
// We do NOT read metaData.project_colors here — per the product decision the
// new theming system is strictly either/or, no merging. Instead we write a
// small set of legacy-named aliases pointing at the new theme.css vars so
// existing LESS files (using `--nav-bg`, `--tab-bg`, ...) keep working
// unchanged.
const setUpStylesNewTheme = (metaData: AppMetaData): void => {
	const aliases: Record<string, string> = {
		// Navigation surfaces
		// title_bar_background inherits colors.background, which theme.css emits as
		// `var(--background-color)`. This bundle repurposes --background-color for the page surface
		// (60 LESS sites read it that way, and it is re-asserted inline below), so aliasing the title
		// bar with var() would paint it the page colour instead of the brand base. Read the -rgb
		// companion, which chains to --background-color-rgb — a name the bundle never declares.
		// Falls back to the var() alias for keyword / 8-digit-alpha colours, which emit no companion.
		'--nav-bg':
			rgbTripletToHex(getStyleProperty('--title-bar-background-color-rgb')) || 'var(--title-bar-background-color)',
		'--title-color': 'var(--title-bar-color)',
		'--tab-bg': 'var(--tab-bar-background-color)',
		// Tab text colour — contrast pair for --tab-bar-background-color, supplied by the registry's tab_bar_content slot.
		'--tab-content-color': 'var(--tab-bar-content-color)',
		'--tab-content-color-selected': 'var(--tab-bar-content-color)',

		// Surface colours (warning, border, secondary button) — registered after
		// sc-144906's first cut via sc-144305 in mytours-web. Until then these
		// fell through to LESS :root defaults.
		'--warning-color': 'var(--warning-background-color)',
		'--border-base-rbg': 'var(--border-base-color)',
		'--border-shadow': 'color-mix(in srgb, var(--border-base-color) 18%, transparent)',
		'--button-secondary-bg': 'var(--secondary-button-button-background)',
		'--button-secondary-border': 'var(--secondary-button-button-outline)',
		'--button-secondary-bg-hover': 'color-mix(in srgb, var(--secondary-button-button-background) 70%, transparent)',

		// Audio player surfaces — map LESS-friendly aliases to the theme.css-emitted
		// names from the registry's fullscreen_audio_player_* / bar_audio_player_*
		// keys. The mini bar and fullscreen player have separate font slots so each
		// surface gets its own colour pair from the YAML; sub-slots (title, eyebrow,
		// time, transcript) are read directly by name in the v2 LESS.
		'--audio-player-fullscreen-bg': 'var(--fullscreen-audio-player-background-color)',
		'--audio-player-fullscreen-color': 'var(--fullscreen-audio-player-color)',
		'--audio-player-bar-bg': 'var(--bar-audio-player-background-color)',
		'--audio-player-bar-color': 'var(--bar-audio-player-color)',
		'--audio-player-transcript-bg': 'var(--fullscreen-audio-player-transcript-background-color)',
		'--audio-player-transcript-color': 'var(--fullscreen-audio-player-transcript-color)',
		'--audio-player-now-playing': 'var(--audio-player-now-playing-color)',

		// Buttons — theme.css emits --buttons-button-background and
		// --start-button-button-background via the buttons / start_button registry
		// families. Hover state is derived at CSS time so we trace back to the new
		// theme's resolved value, not a project_colors hex. --button-text-color
		// resolves the text colour from the registry's button_font.color so LESS
		// rules that previously paired --button-bg with --title-color (assuming a
		// dark button surface) no longer render white-on-white on the registry's
		// light default.
		//
		// The alias MUST NOT be named --button-color: theme.css *also* emits a raw
		// --button-color (= var(--body-color)) for any project with a theme record,
		// so LESS reading var(--button-color, var(--title-color, #fff)) directly
		// picked up that dark body colour on legacy projects that never enabled
		// new_theming — rendering dark text on a dark button (Ingenium, sc-145927
		// class). Routing through --button-text-color (a name theme.css doesn't
		// emit) keeps the token strictly behind the flag; legacy LESS falls back to
		// --title-color (#fff) as before.
		'--button-bg': 'var(--buttons-button-background)',
		'--button-text-color': 'var(--buttons-button-font-color)',
		'--button-outline': 'var(--buttons-button-outline)',
		// Width sentinel: LESS uses `var(--button-border-width, 0)` so legacy
		// customers get a zero-width (truly absent) border. New-theme customers
		// get a 1px border whose colour is --button-outline (transparent if the
		// registry didn't supply one) — matches the new design intent without
		// leaking a 2px layout shift to anyone still on the legacy theme.
		'--button-border-width': '1px',
		// Icon colour for "selected" indicators (e.g. language-modal checkmark).
		// Legacy used --button-action-bg (a vivid CTA hex from project_colors).
		// The new registry's start_button family defaults to white, so we route
		// these icons through --secondary-color instead. LESS sites that read
		// this variable also accept --button-action-bg as their legacy fallback.
		'--selection-icon-color': 'var(--secondary-color)',

		// Font families — stqry-web LESS uses --<key>-font-<field> with a
		// duplicated "font" segment (e.g. --body-font-font-family) inherited
		// from a pre-normalisation registry. theme.css now emits the deduped
		// name (--body-font-family); we bridge the two until LESS migrates.
		'--body-font-font-family': 'var(--body-font-family)',
		'--body-font-font-weight': 'var(--body-font-weight)',
		'--body-font-font-style': 'var(--body-font-style)',
		'--body-font-font-size': 'var(--body-font-size)',
		'--heading-font-font-family': 'var(--heading-font-family)',
		'--heading-font-font-weight': 'var(--heading-font-weight)',
		'--heading-font-font-style': 'var(--heading-font-style)',
		'--heading-font-font-size': 'var(--heading-font-size)',
		'--title-bar-font-font-family': 'var(--title-bar-font-family)',
		'--title-bar-font-font-weight': 'var(--title-bar-font-weight)',
		'--title-bar-font-font-style': 'var(--title-bar-font-style)',
		'--title-bar-font-font-size': 'var(--title-bar-font-size)',
		'--block-heading-font-font-family': 'var(--block-heading-font-family)',
		'--block-heading-font-font-weight': 'var(--block-heading-font-weight)',
		'--block-heading-font-font-style': 'var(--block-heading-font-style)',
		'--block-heading-font-font-size': 'var(--block-heading-font-size)',
		'--tour-heading-font-font-family': 'var(--tour-heading-font-family)',
		'--tour-heading-font-font-weight': 'var(--tour-heading-font-weight)',
		'--tour-heading-font-font-style': 'var(--tour-heading-font-style)',
		'--tour-heading-font-font-size': 'var(--tour-heading-font-size)',
		'--button-bg-hover': 'color-mix(in srgb, var(--buttons-button-background) 70%, transparent)',
		'--button-action-bg': 'var(--start-button-button-background)',
		// Start ("action") button text colour. Like --button-color above, this MUST
		// be a JS-written alias rather than read straight off the raw theme.css var:
		// theme.css is emitted for any project with a theme record, so .start-btn
		// reading --start-button-button-font-color directly would pick up the
		// registry's dark default (#111827) even on legacy projects that never
		// enabled new_theming — rendering invisible text on a dark action button.
		// Routing through this alias keeps the token strictly behind the flag; legacy
		// LESS falls back to --title-color as before.
		'--button-action-color': 'var(--start-button-button-font-color)',

		// Tour stop footer (Previous / Next / Finish bar). Same scoping rule as the
		// buttons above: theme.css emits the tour_bar / tour_bar_background registry
		// slots for any project with a theme record, and tour_bar_background
		// inherits from tab_bar_background → primary. Reading those raw vars
		// directly in tour-stop-footer LESS would paint the footer in the
		// tab-bar/brand colour on legacy projects that never enabled new_theming.
		// Route them through JS aliases so the legacy LESS falls back to
		// --background-color / --body-color (and its prior hardcoded fonts) instead.
		// Font props use the duplicated "font" segment to match the bridge naming
		// of --tour-heading-font-* / --body-font-* above.
		'--tour-bar-bg': 'var(--tour-bar-background-color)',
		'--tour-bar-content-color': 'var(--tour-bar-color)',
		'--tour-bar-font-font-family': 'var(--tour-bar-font-family)',
		'--tour-bar-font-font-style': 'var(--tour-bar-font-style)',
		'--tour-bar-font-font-weight': 'var(--tour-bar-font-weight)',
		'--tour-bar-font-font-size': 'var(--tour-bar-font-size)',
		'--tour-bar-font-letter-spacing': 'var(--tour-bar-letter-spacing)',

		// Block / tour heading text colour. Same scoping rule as the buttons above:
		// theme.css emits --block-heading-color (= var(--default-color)) and
		// --tour-heading-color (= var(--heading-color) → --default-color) for any
		// project with a theme record. The heading LESS reading those raw vars
		// directly painted headings in the registry's dark --default-color (#000000)
		// on legacy projects with a dark background + white body text — the H2 stays
		// black on black (Ingenium, sc-145927 class). Route them through aliases
		// theme.css doesn't emit so the heading LESS falls back to --body-color on
		// legacy; new-theme projects get the registry heading colour.
		'--block-heading-text-color': 'var(--block-heading-color)',
		'--tour-heading-text-color': 'var(--tour-heading-color)',

		// mytours.less declares --body-color and --background-color on :root as pre-theme
		// defaults, and the app bundle loads AFTER theme.css — so theme.css's same-named,
		// same-specificity :root emits lose the cascade and the theme's values never apply.
		// (Invisible until now: the registry defaults are white/near-black, same as the
		// bundle's.) Re-assert both inline (inline style beats any stylesheet) from
		// theme.css names the bundle does NOT declare: --page-background-color-rgb carries
		// the colors.page_background slot, and --default-color carries the fonts.default
		// colour that --body-color inherits from.
		//
		// The page background is read from the -rgb companion, not --page-background-color:
		// an unset page_background inherits from colors.background, which theme.css emits as
		// `--page-background-color: var(--background-color)` — substituting straight back
		// through the name the bundle shadows. The -rgb companion is built from the resolver's
		// literal mode and chains to the parent's own companion
		// (`var(--background-color-rgb)`), a name the bundle never declares, so it carries the
		// theme's real value whether page_background is set or inherited.
		//
		// These two MUST be concrete hexes, not var() refs: JS consumers read them back via
		// getStyleProperty and derive colours with tinycolor (e.g. CollectionTour's fade-out
		// gradient), and tinycolor parses a var() string as black. Static fallbacks keep a
		// theme.css that predates those slots on today's bundle values. Limitation: a theme
		// that overrides fonts.body.color specifically (not fonts.default) emits a literal
		// --body-color which is still shadowed — that override does not surface here.
		// The --app-background-background-value read is transitional. It is the retired
		// variables.app_background.background field, which this bundle used to treat as the page
		// background; reading it keeps rendering unchanged on a theme.css that predates
		// colors.page_background, so the two repos can deploy in either order. Drop it once
		// mytours-web is out everywhere — after that it only appears on themes with a stale DB row.
		'--background-color':
			rgbTripletToHex(getStyleProperty('--page-background-color-rgb')) ||
			getStyleProperty('--app-background-background-value').trim() ||
			'#ffffff',
		'--body-color': getStyleProperty('--default-color').trim() || '#1b1919',

		// Body-color derivations (theme.css emits --body-color via the body font slot).
		// Percentages mirror the legacy semantics: fade(body, 0.4) → 40% over transparent.
		'--body-color-hover': 'color-mix(in srgb, var(--body-color) 40%, transparent)',
		'--placeholder-bg': 'color-mix(in srgb, var(--body-color) 17%, transparent)',
		'--border-color': 'color-mix(in srgb, var(--body-color) 15%, transparent)',
		'--border-light': 'color-mix(in srgb, var(--body-color) 10%, transparent)',
		'--border-overlay': 'color-mix(in srgb, var(--body-color) 18%, transparent)',
		'--card-box-shadow-1': 'color-mix(in srgb, var(--body-color) 5%, transparent)',
		'--card-box-shadow-2': 'color-mix(in srgb, var(--body-color) 3%, transparent)',
		'--card-box-shadow-3': 'color-mix(in srgb, var(--body-color) 10%, transparent)',
		'--extended-body-color': 'color-mix(in srgb, var(--body-color) 90%, black)',

		// Background-color derivations (theme.css emits --background-color).
		'--background-color-hover': 'color-mix(in srgb, var(--background-color) 97%, black)',
		'--extended-background-color': 'color-mix(in srgb, var(--background-color) 90%, black)',

		// Collection list-item cards. theme.css emits the collection_list_item registry
		// group (--collection-list-item-radius / -gradient-color / -gradient-color-rgb /
		// -gradient-opacity) and the collection_list_item_title colour for any project
		// with a theme record, and @mytours/components' tokens.css maps each
		// --sq-collection-list-item-* token straight onto those raw names — so a legacy
		// project whose theme record carries collection_list_item rows (e.g. a
		// transparent "hide title" colour) would restyle, or blank, its card titles
		// without ever enabling new_theming. Route the group through these JS-only alias
		// names (theme.css doesn't emit them); card-image LESS re-declares the --sq-*
		// tokens from these with fallbacks to the pre-theming card, so legacy renders
		// unchanged. Inner fallbacks are the registry defaults, for a flag-on project
		// whose theme.css predates the collection_list_item slots.
		'--list-card-radius': 'var(--collection-list-item-radius, 0.375rem)',
		'--list-card-gradient-color': 'var(--collection-list-item-gradient-color, #000000)',
		'--list-card-gradient-color-rgb': 'var(--collection-list-item-gradient-color-rgb, 0 0 0)',
		'--list-card-gradient-opacity': 'var(--collection-list-item-gradient-opacity, 0.35)',
		'--list-card-title-color': 'var(--collection-list-item-title-color, var(--reverse-color, #ffffff))',

		// Settings rows / transcript-available label / audio-card title typography.
		// Same scoping rule as the tour_bar / collection-list-item aliases above:
		// theme.css emits the raw font keys (--settings-label-font-family,
		// --settings-label-color, ...) for any project with a theme record, so
		// reading those directly in LESS would restyle these surfaces on legacy
		// projects that never enabled new_theming. Route them through JS-only alias
		// names theme.css doesn't emit — the duplicated "font" segment for font
		// fields (--settings-label-font-font-family) and -text-color for colour
		// (--settings-label-text-color) — so the new typography stays strictly
		// behind the flag; the LESS reads each with a fallback to its current
		// hardcode (wired in a later task — sc-147919).
		'--settings-label-font-font-family': 'var(--settings-label-font-family)',
		'--settings-label-font-font-style': 'var(--settings-label-font-style)',
		'--settings-label-font-font-weight': 'var(--settings-label-font-weight)',
		'--settings-label-font-font-size': 'var(--settings-label-font-size)',
		'--settings-label-font-line-height': 'var(--settings-label-line-height)',
		'--settings-label-font-letter-spacing': 'var(--settings-label-letter-spacing)',
		'--settings-label-text-color': 'var(--settings-label-color)',
		'--settings-value-font-font-family': 'var(--settings-value-font-family)',
		'--settings-value-font-font-style': 'var(--settings-value-font-style)',
		'--settings-value-font-font-weight': 'var(--settings-value-font-weight)',
		'--settings-value-font-font-size': 'var(--settings-value-font-size)',
		'--settings-value-font-line-height': 'var(--settings-value-line-height)',
		'--settings-value-font-letter-spacing': 'var(--settings-value-letter-spacing)',
		'--settings-value-text-color': 'var(--settings-value-color)',
		'--transcript-available-font-font-family': 'var(--transcript-available-font-family)',
		'--transcript-available-font-font-style': 'var(--transcript-available-font-style)',
		'--transcript-available-font-font-weight': 'var(--transcript-available-font-weight)',
		'--transcript-available-font-font-size': 'var(--transcript-available-font-size)',
		'--transcript-available-font-line-height': 'var(--transcript-available-line-height)',
		'--transcript-available-font-letter-spacing': 'var(--transcript-available-letter-spacing)',
		'--transcript-available-text-color': 'var(--transcript-available-color)',
		'--audio-card-title-font-font-family': 'var(--audio-card-title-font-family)',
		'--audio-card-title-font-font-style': 'var(--audio-card-title-font-style)',
		'--audio-card-title-font-font-weight': 'var(--audio-card-title-font-weight)',
		'--audio-card-title-font-font-size': 'var(--audio-card-title-font-size)',
		'--audio-card-title-font-line-height': 'var(--audio-card-title-line-height)',
		'--audio-card-title-font-letter-spacing': 'var(--audio-card-title-letter-spacing)',
		'--audio-card-title-text-color': 'var(--audio-card-title-color)',

		// Tour overview header (CollectionTour, isTransparentHeader). Two states, each a
		// (background, font) pair from the registry's tour_overview_header_start /
		// tour_overview_header_end (font) + *_background (color) slots. Aliased under names
		// theme.css never emits (-bg / -text-color) so the overview LESS reads them strictly
		// behind the flag; legacy / no-theme-record falls back to today's values — the
		// overview header carries .with-bg-color, so un-scrolled is transparent + --title-color
		// icons and scrolled is the --nav-bg bar + --title-color icons. Start color defaults to
		// white in the registry so icons stay legible over the hero. The overview-header icon
		// drop-shadow is dropped in BOTH states (see header/styles.less) — legibility now comes
		// from the themed background/icon contrast, not a halo. That filter:none is unconditional,
		// so flag-off projects lose the halo too; this is an accepted, documented change (only the
		// overview header matches, blast radius is tiny) — see the design doc's backward-compat note.
		//
		// Each alias carries an INNER fallback to today's value. These four registry slots ship
		// from mytours-web independently (sc-148984 Task 0), but this alias is written for EVERY
		// new_theming_system project. A flag-on project whose theme.css predates the slots (e.g.
		// stqry-web deployed before the mytours-web slots) would otherwise set the alias to an
		// unresolvable var() — and because the alias IS set (just invalid), the LESS-level
		// fallback in `var(--tour-overview-header-start-bg, …)` is bypassed (a set-but-invalid
		// custom property does not trigger the consuming var()'s fallback), so the declaration
		// goes invalid-at-computed-value-time: the scrolled bar collapses to transparent and the
		// icon colour resets. The inner fallback makes that deploy-gap degrade gracefully to
		// today's value (transparent / --nav-bg / --title-color) instead of glitching.
		'--tour-overview-header-start-bg': 'var(--tour-overview-header-start-background-color, transparent)',
		'--tour-overview-header-start-text-color': 'var(--tour-overview-header-start-color, var(--title-color))',
		// Same chain as --nav-bg: tour_overview_header_end_background inherits title_bar_background,
		// so it routes through --background-color too.
		'--tour-overview-header-end-bg':
			rgbTripletToHex(getStyleProperty('--tour-overview-header-end-background-color-rgb')) ||
			'var(--tour-overview-header-end-background-color, var(--nav-bg))',
		'--tour-overview-header-end-text-color': 'var(--tour-overview-header-end-color, var(--title-color))',

		'--pay-modal-background': 'var(--payment-modal-background-color)',
		'--pay-modal-text': 'var(--payment-modal-text-color)',
		'--pay-modal-border': 'var(--payment-modal-border-color)',
		'--pay-modal-accent': 'var(--payment-modal-accent-color)',
		'--pay-modal-button': 'var(--payment-modal-button-color)',
		'--pay-modal-on-button': 'var(--payment-modal-on-button-color)',
	}

	// Image vars are NOT theme-color-derived; preserve the legacy behaviour
	// so the project's background_image_media_item still applies. Build the
	// values using the same helpers the legacy branch uses below.
	//
	// The backdrop behind the centred content column is its own themed surface, independent of the
	// page background — colors.app_background inherits colors.background, not page_background. Feed
	// it to the .background-image() mixin, which otherwise darkens the page background by 10% (all a
	// single-colour legacy theme could express). Read via the -rgb companion for the same reason as
	// the page background: an inherited colour emits `var(--background-color)`, which substitutes
	// back through the name mytours.less shadows. Left unset when theme.css predates the slot, so
	// the mixin keeps its --extended-background-color fallback.
	const appBackground = rgbTripletToHex(getStyleProperty('--app-background-color-rgb'))
	if (appBackground) {
		aliases['--app-bg'] = appBackground
	}

	// app_background.display from the theme registry (theme.css emits it on
	// :root as --app-background-display) lets a theme hide the blurred app
	// icon backdrop. 'none' → emit `background-image: none` so the
	// .background-image() mixin falls back to the plain --app-bg colour.
	// Anything else (the registry default 'block', or an
	// absent var on a project whose theme.css predates this slot) keeps the
	// icon backdrop — a safe, backwards-compatible default. (sc-72156)
	const appBackgroundDisplay = getStyleProperty('--app-background-display').trim()
	if (appBackgroundDisplay === 'none') {
		aliases['--background-image'] = 'none'
	} else {
		let backgroundImageSrc = ''
		if (metaData.icon_media_item?.file) {
			backgroundImageSrc = generateImageUrl(metaData.icon_media_item.file, {
				imageSizePreset: ImageSizePreset.Fullscreen,
				blur: 10,
			})
		}
		aliases['--background-image'] = `url(${backgroundImageSrc})`
	}

	let contentBackgroundImageSrc = ''
	if (metaData.background_image_media_item) {
		contentBackgroundImageSrc = generateImageUrl(metaData.background_image_media_item.file, {
			imageSizePreset: ImageSizePreset.Fullscreen,
			focalPoint: metaData.background_image_media_item.file?.focal_point,
		})
	}
	aliases['--content-background-image'] = `url(${contentBackgroundImageSrc})`

	aliases['--map-content-shadow'] = 'rgba(0,0,0,0.1)'

	for (const [name, value] of Object.entries(aliases)) {
		document.documentElement.style.setProperty(name, value)
	}
}

// `beta_features` is not declared on the installed GetAppShowResponse yet, hence the cast
export const hasNewThemingSystem = (metaData: AppMetaData): boolean =>
	(metaData as { beta_features?: { new_theming_system?: boolean } }).beta_features?.new_theming_system === true

const setUpStyles = (metaData: AppMetaData) => {
	if (hasNewThemingSystem(metaData)) {
		setUpStylesNewTheme(metaData)
		return
	}

	const secondaryBgColor = colorMix(
		metaData.project_colors.background_color,
		metaData.project_colors.button_background_color,
		15,
		40,
	)

	const isBgDark = isDark(metaData.project_colors.background_color)

	let backgroundImageSrc = ''

	if (metaData.icon_media_item?.file) {
		backgroundImageSrc = generateImageUrl(metaData.icon_media_item?.file, {
			imageSizePreset: ImageSizePreset.Fullscreen,
			blur: 10,
		})
	}

	let contentBackgroundImageSrc = ''

	if (metaData.background_image_media_item) {
		contentBackgroundImageSrc = generateImageUrl(metaData.background_image_media_item?.file, {
			imageSizePreset: ImageSizePreset.Fullscreen,
			focalPoint: metaData.background_image_media_item.file?.focal_point,
		})
	}

	const styles: Style = {
		'--background-color': metaData.project_colors.background_color,
		'--background-color-hover': darkenOrLighten(metaData.project_colors.background_color, 3),
		'--background-image': `url(${backgroundImageSrc})`,
		'--content-background-image': `url(${contentBackgroundImageSrc})`,
		'--extended-background-color': darkenOrLighten(metaData.project_colors.background_color, 10),
		'--body-color': metaData.project_colors.body_text_color,
		'--body-color-hover': fade(metaData.project_colors.body_text_color, 0.4),
		'--extended-body-color': darkenOrLighten(metaData.project_colors.body_text_color, 10),
		'--secondary-color': colorMix(
			metaData.project_colors.background_color,
			metaData.project_colors.button_background_color,
			15,
			40,
		),
		'--placeholder-bg': fade(metaData.project_colors.body_text_color, 0.17),
		'--nav-bg': metaData.project_colors.header_color,
		'--tab-bg': metaData.project_colors.tab_bar_color,
		'--tab-content-color': fade(metaData.project_colors.tab_bar_content_color, 0.65),
		'--tab-content-color-selected': metaData.project_colors.tab_bar_content_color,
		'--button-bg': metaData.project_colors.button_background_color,
		'--button-bg-hover': fade(metaData.project_colors.button_background_color, 0.7),
		'--button-action-bg': metaData.project_colors.action_button_background_color,
		'--warning-color': metaData.project_colors.warning_background_color,
		'--button-secondary-bg': secondaryBgColor,
		'--button-secondary-bg-hover': fade(secondaryBgColor, 0.7),
		'--button-secondary-border': colorMix(secondaryBgColor, metaData.project_colors.body_text_color, 20, 10),
		'--border-base-rbg': metaData.project_colors.border_base_color,
		'--border-color': fade(metaData.project_colors.body_text_color, isBgDark ? 0.3 : 0.15),
		'--border-light': fade(metaData.project_colors.body_text_color, isBgDark ? 0.2 : 0.1),
		'--border-overlay': fade(metaData.project_colors.body_text_color, isBgDark ? 0.32 : 0.18),
		'--map-content-shadow': 'rgba(0,0,0,0.1)',
		'--border-shadow': fade(metaData.project_colors.border_base_color, 0.18),
		'--card-box-shadow-1': fade(metaData.project_colors.body_text_color, 0.05),
		'--card-box-shadow-2': fade(metaData.project_colors.body_text_color, 0.03),
		'--card-box-shadow-3': fade(metaData.project_colors.body_text_color, 0.1),
	}

	// Audio player tokens (sc-143491). Backend may not ship these fields
	// yet — only emit the var when we have a value so the CSS-side
	// `var(--audio-player-bg, <legacy-fallback>)` keeps untouched themes
	// pixel-identical to before. The keys mirror the builder's registry
	// in client-v4 (`features/themes/registry.ts`).
	const playerColors = metaData.project_colors as unknown as Record<string, string | undefined>
	const audioPlayerVars: Record<string, string | undefined> = {
		'--audio-player-fullscreen-bg': playerColors.audio_player_background_color,
		'--audio-player-fullscreen-color': playerColors.audio_player_content_color,
		'--audio-player-bar-bg': playerColors.audio_player_bar_background_color,
		'--audio-player-bar-color': playerColors.audio_player_bar_content_color,
		'--audio-player-transcript-bg': playerColors.audio_player_transcript_background_color,
		'--audio-player-transcript-color': playerColors.audio_player_transcript_content_color,
		'--audio-player-now-playing': playerColors.audio_player_now_playing_color,
	}
	const audioPlayerVariables = (metaData as unknown as { audio_player_variables?: Record<string, string | undefined> })
		.audio_player_variables
	if (audioPlayerVariables) {
		audioPlayerVars['--audio-player-now-playing-border-width'] = audioPlayerVariables.now_playing_border_width
		audioPlayerVars['--audio-player-inactive-opacity'] = audioPlayerVariables.inactive_opacity
		audioPlayerVars['--audio-player-next-label-opacity'] = audioPlayerVariables.next_label_opacity
	}
	for (const [name, value] of Object.entries(audioPlayerVars)) {
		if (value) styles[name] = value
	}

	for (const propertyName of Object.keys(styles)) {
		document.documentElement.style.setProperty(propertyName, styles[propertyName])
	}
}

export const getStyleProperty = (propertyValue: keyof Style): string =>
	document.documentElement.style.getPropertyValue(propertyValue) ||
	getComputedStyle(document.documentElement).getPropertyValue(propertyValue)

/**
 * Theme/Styles
 */

// The sagas `yield call` this before dispatching `loadedMetaDataComplete`, so the returned promise
// holds back the first render until the theme string overrides are in (see syncThemeStrings).
export function changeTheme(metaData: AppMetaData): Promise<void> {
	setUpStyles(metaData)
	return syncThemeStrings(metaData)
}

/**
 * Download file
 */

/**
 * Downloads an audio file and saves to internal storage. The result includes the path to downloaded file.
 * @param appId the app id - used in file path
 * @param collectionId the collection ite - used in file path
 * @param url the url of audio file to download
 * @returns Promise that returns the path to download file if successful
 */
export function downloadFile(_type: FileType, _fileName: string, url: string): Promise<string> {
	return new Promise((resolve, reject) => {
		try {
			resolve(url.split('?')[0])
		} catch (error) {
			reject(error)
		}
	})
}

/**
 * Calculates the download path for a file with given info
 * @param collectionId collection id
 * @param collectionItemId collection item id
 * @param fileName name of file including file suffix
 */
export function getFileDownloadPath(_type: FileType, _fileName: string): string {
	// Sync Content is native-only; mobile web streams from the network and never downloads to disk.
	return ''
}

/**
 * PreFetches an Image file and saves to disk cache as part of the Sync Content feature. The result includes whether operation was successful.
 * @param id the image id - used in url
 * @param filename the image file name - used in url
 * @param isForVideo `true` if url is for video thumbnail (url is different path)
 * @returns Promise that returns `true` if successful
 */
export function preDownloadImage(_id: number, _filename: string, _isForVideo: boolean): Promise<boolean> {
	// Sync Content is native-only; there is no disk cache on mobile web to prefetch into.
	return Promise.resolve(true)
}

export const getPlatform = (): DevicePlatform => DEVICE_PLATFORM_KEY_MOBILE_WEB

export function getDeviceUUID(): string {
	return '1' // DeviceInfo.getUniqueID()
}

export const setDataToLocalStorage = (key: string, data: any, persist?: boolean): Promise<void> =>
	new Promise((resolve, reject) => {
		try {
			const value = JSON.stringify(data)

			if (persist) {
				localStorage.setItem(key, value)
			} else {
				sessionStorage.setItem(key, value)
			}
			resolve()
		} catch (error) {
			reject(error)
		}
	})

export const getDataFromLocalStorage = (key: string, persist?: boolean, skipVersion?: boolean): Promise<any> =>
	new Promise((resolve, reject) => {
		try {
			if (skipVersion) {
				const storageItems = persist ? { ...localStorage } : { ...sessionStorage }
				let result

				Object.keys(storageItems).forEach(itemKey => {
					if (itemKey.startsWith(key)) {
						const data = persist ? localStorage.getItem(itemKey) : sessionStorage.getItem(itemKey)
						result = JSON.parse(data)
						resolve(result)
						return
					}
				})

				if (!result) {
					resolve(undefined)
					return
				}
			} else {
				const data = persist ? localStorage.getItem(key) : sessionStorage.getItem(key)
				const result = JSON.parse(data)
				resolve(result)
			}
		} catch (error) {
			reject(error)
		}
	})

export const removeDataFromLocalStorage = (key: string | string[], persist?: boolean): Promise<void> =>
	new Promise((resolve, reject) => {
		try {
			const storageItems = persist ? { ...localStorage } : { ...sessionStorage }

			Object.keys(storageItems).forEach(itemKey => {
				if (Array.isArray(key)) {
					key.forEach(
						item =>
							itemKey.startsWith(item) &&
							(persist ? localStorage.removeItem(itemKey) : sessionStorage.removeItem(itemKey)),
					)
				} else {
					if (itemKey.startsWith(key)) {
						if (persist) {
							localStorage.removeItem(itemKey)
						} else {
							sessionStorage.removeItem(itemKey)
						}
					}
				}
			})
			resolve()
		} catch (error) {
			reject(error)
		}
	})

export const getCurrentLanguage = (): string => getCurrentLanguageCode(getStore().getState())

export const getDirMode = (): 'rtl' | 'ltr' => {
	let dirMode: 'rtl' | 'ltr'

	try {
		dirMode = isRTL() ? 'rtl' : 'ltr'
	} catch {
		dirMode = 'ltr'
	}

	return dirMode
}

export function generateMapboxStyleUrl(styleId: string): string {
	const mapboxUserId: string = ENV.MAP_USER_ID
	return `mapbox://styles/${mapboxUserId}/${styleId}`
}

export const formatCurrency = (currency: string, value: number) => {
	const formatter = new Intl.NumberFormat(getCurrentLanguage(), {
		style: 'currency',
		currency,
	})

	if (!value) {
		return `${strings.free}`.toUpperCase()
	}

	return formatter.format(value / 100) // we don't need to use cents
}

export const hexToRGB = (hex: string, opacity = 1) => {
	const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

	if (match) {
		return `rgba(${parseInt(match[1], 16)},${parseInt(match[2], 16)},${parseInt(match[3], 16)},${opacity})`
	}

	return undefined
}

export const isWideImage = (file: FileRec): boolean => {
	if (file?.width > file?.height) {
		return true
	}

	return false
}

const getFormattedDuration = (data: FormattedDurationObject) => {
	// Object {data} has three properties (days, hours, minutes)
	// We are getting only two (days and hours) or (hours or minutes)
	// So, we need to get translation string, depends on properties
	const dataKeys = Object.keys(data)
	const key1 = dataKeys[0]
	const key2 = dataKeys[1]

	const singularKey1 = key1 && key1.substring(0, key1.length - 1)
	const singularKey2 = key2 && key2.substring(0, key2.length - 1)

	let keyEndpoint

	if (data[key1] > 1 && data[key2] > 1) {
		// days, hours or hours, minutes
		keyEndpoint = `${key1}_and_${key2}`
	} else if (data[key1] > 1 && data[key2] === 1) {
		// days, hour or hours, minute
		keyEndpoint = `${key1}_and_${singularKey2}`
	} else if (data[key1] === 1 && data[key2] > 1) {
		// day, hours or hour, minutes
		keyEndpoint = `${singularKey1}_and_${key2}`
	} else if (data[key1] === 1 && data[key2] === 1) {
		// day, hour or hour, minute
		keyEndpoint = `${singularKey1}_and_${singularKey2}`
	} else if (data[key2] === 1) {
		// only hour or minute
		keyEndpoint = singularKey2
	} else if (data[key2] > 1) {
		// only hours or minutes
		keyEndpoint = key2
	} else if (data[key1] === 1) {
		// only day, hour
		keyEndpoint = singularKey1
	} else if (data[key1] > 1) {
		// only days, hours
		keyEndpoint = key1
	}

	return keyEndpoint ? strings.formatString(strings[`duration_${keyEndpoint}`], { ...data }).toString() : ''
}

enum Unit {
	Imperial = 'imperial',
	Metric = 'metric',
}
export type UnitType = Unit.Imperial | Unit.Metric

/**
 * @deprecated use distance unit from app settings
 */
export const getDistanceUnit = (): UnitType => {
	const language = getCurrentLanguage()
	const country = language && typeof language === 'string' ? language.split('-')[1] : undefined

	if (country !== undefined && ['uk', 'gb', 'us', 'mm', 'lr'].includes(country.toLowerCase())) {
		return Unit.Imperial
	}

	return Unit.Metric
}

export const format = {
	duration: (duration: number): string => {
		if (!duration || isNaN(duration)) {
			return ''
		}

		// 1440 - minutes in day (60 * 24)
		const durationDays = Math.floor(duration / 1440) || null
		const durationHours = Math.floor((duration - durationDays * 1440) / 60) || null
		const durationMinutes = Math.round(duration % 60) || null

		if (durationDays) {
			return getFormattedDuration({
				days: durationDays,
				hours: durationHours,
			})
		} else if (durationHours) {
			return getFormattedDuration({
				hours: durationHours,
				minutes: durationMinutes,
			})
		} else if (durationMinutes) {
			return getFormattedDuration({
				minutes: durationMinutes,
			})
		} else {
			return ''
		}
	},
	distance: (meters: number, distanceUnit: GetAppShowResponse.DistanceUnitEnum): string => {
		switch (distanceUnit) {
			case GetAppShowResponse.DistanceUnitEnum.Mi: {
				const mi = meters / 1609.344
				if (mi >= 0.1) {
					return strings.formatString(strings.distance_miles_s, {
						distance: parseFloat(mi.toFixed(mi >= 100 ? 0 : 1)),
					}) as string
				}
				return strings.formatString(strings.distance_feet_s, { distance: (mi * 5280).toFixed(0) }) as string
			}
			case GetAppShowResponse.DistanceUnitEnum.Km: {
				if (meters >= 500) {
					return strings.formatString(strings.distance_kilometers_s, {
						distance: parseFloat((meters / 1000).toFixed(meters >= 100000 ? 0 : 1)),
					}) as string
				}
				return strings.formatString(strings.distance_meters_s, { distance: meters.toFixed(0) }) as string
			}
		}
	},
}

export const getErrorMessage = (error: Error): string | undefined => {
	if (isOfflineError(error)) {
		return strings.network_error
	} else {
		return strings.error_occurred
	}
}

export const formatNumberStyle = (property: number | string) =>
	typeof property === 'string' || (!isNaN(property) && property !== null) ? property : undefined

export const convertCamelCaseKeysToSnakeCase = (obj: any): any => {
	const snakeCaseParams = {}
	Object.keys(obj).forEach(key => {
		const snakeCaseKey = key
			.replace(/([A-Z])/g, ' $1')
			.split(' ')
			.join('_')
			.toLowerCase()
		snakeCaseParams[snakeCaseKey] = obj[key]
	})

	return snakeCaseParams
}

export const validateUrl = (link: string): URL => {
	let url: URL
	try {
		url = new URL(link)
	} catch (error) {
		console.warn('url not valid')
	}

	return url
}

export const waitForSomething = (callback: () => unknown, timeout = 500, maxAttempts = 10): Promise<unknown> =>
	new Promise((resolve, reject) => {
		let attempts = 0
		const waitingInterval = setInterval(() => {
			if (attempts === maxAttempts) {
				clearInterval(waitingInterval)
				reject()
			}

			if (callback()) {
				clearInterval(waitingInterval)
				resolve(callback())
			} else {
				attempts++
			}
		}, timeout)
	})

export const isPageInsideIFrame = (): boolean => window.location !== window.parent.location

export const isSafariBrowser = (): boolean => {
	const browser = getUAParser().getBrowser()
	if (browser.name === 'Safari' || browser.name === 'Mobile Safari') {
		return true
	}
	return
}

export const isIOSSafariBrowser = (): boolean => {
	const browser = getUAParser().getBrowser()
	if (browser.name === 'Mobile Safari') {
		return true
	}
	return false
}

export const isMobileOrTabletDevice = (): boolean => {
	const device = getUAParser().getDevice()
	return device.type === 'mobile' || device.type === 'tablet'
}

export const containsEmbeddedURL = (url: string): boolean => {
	const res = url.match(/=(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:\/?#[\]@!\$&'\(\)\*\+,;=.%]+/g)
	return res !== null
}
